import React, { ComponentProps, useState } from 'react';
import { Modal, Text, ModalFooter, Button, Alert, useTable, Table, UiKit } from '@datavant/uikit';
import {
    ChakraProvider,
    VStack,
    useToast,
    Box,
    Collapse,
    useDisclosure,
    Switch,
    HStack,
    AbsoluteCenter,
    Center,
    Link,
} from '@chakra-ui/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCaretDown, faCaretUp, faTriangleExclamation } from '@fortawesome/free-solid-svg-icons';
import { trpc, tRPCProxyClient } from '../../../utils/trpc';
import { ColumnDef, ColumnFiltersState, createColumnHelper, getCoreRowModel } from '@tanstack/react-table';
import { NonEmbeddedImplementationStatus } from '../../../generated/request-manager-api';

export const ScannedDocumentsModal = ({
    isOpen,
    onClose,
}: {
    isOpen: boolean;
    onClose: ComponentProps<typeof Modal>['onClose'];
}) => {
    const toast = useToast();
    const { isOpen: isEditing, onToggle: toggleEditing } = useDisclosure();
    const { isOpen: isTableOpen, onToggle: toggleTable } = useDisclosure();
    const [pagination, setPagination] = useState({
        pageIndex: 0,
        pageSize: 10,
    });
    const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
    const [excludedCategories, setExcludedCategories] = useState<{ isActive: false; id: number }[]>([]);

    const scannedDocumentCategorySearch = columnFilters.find(
        filter => filter.id === 'documentCategoryName'
    )?.value;

    const utils = trpc.useUtils();

    const { data, refetch, isFetching } = trpc.autofulfill.getScannedDocumentCategoryFeedback.useQuery(
        {
            size: pagination.pageSize,
            page: pagination.pageIndex + 1,
            ...(typeof scannedDocumentCategorySearch === 'string'
                ? { documentCategorySearch: scannedDocumentCategorySearch }
                : {}),
        },
        { keepPreviousData: true }
    );

    const { mutate, isLoading: isMutating } =
        trpc.autofulfill.mutateScannedDocumentCategoryFeedback.useMutation({
            onMutate: async () => {
                await utils.tenant.getTenant.cancel();
                const previousTenantData = utils.tenant.getTenant.getData();
                utils.tenant.getTenant.setData(undefined, old =>
                    old && old !== null && old.digitalMetadata
                        ? {
                              ...old,
                              digitalMetadata: {
                                  ...old.digitalMetadata,
                                  onboardingStatus:
                                      NonEmbeddedImplementationStatus.SCANNED_DOCUMENT_FEEDBACK_COMPLETED,
                              },
                          }
                        : null
                );

                // Return a context object with the snapshotted value so it can be used to reset the cache to a previous state
                // on error
                return { previousTenantData };
            },
            onSuccess: async () => {
                toast({
                    title: 'Scanned document categories confirmed.',
                    status: 'success',
                    duration: 2000,
                    isClosable: true,
                });
                await tRPCProxyClient.autofulfill.updateOnboardingStatus.mutate({
                    status: NonEmbeddedImplementationStatus.SCANNED_DOCUMENT_FEEDBACK_COMPLETED,
                });
                onClose();
            },
            onError: async () => {
                // Force a re-fetch of the tenant data on error
                await utils.tenant.getTenant.invalidate();

                toast({
                    title: 'There was an error confirming scanned document categories. Please try again or reach out to customer service via help.',
                    status: 'error',
                    duration: 2000,
                    isClosable: true,
                });
            },
        });

    const isLoading = isFetching || isMutating;
    const dataNotAvailable = !isLoading && data?.items?.length === 0;

    const columnHelper = createColumnHelper<NonNullable<typeof data>['items'][number]>();

    const columnDefs: ColumnDef<NonNullable<typeof data>['items'][number], any>[] = [
        columnHelper.display({
            id: 'activeSwitch',
            meta: {
                column: {
                    width: '1%',
                },
            },
            cell: ({ row }) => {
                return (
                    <Switch
                        isChecked={
                            excludedCategories.find(cat => cat.id === parseInt(row.id))?.isActive ??
                            row.original.isActive
                        }
                        isDisabled={!isEditing}
                        onChange={() =>
                            setExcludedCategories(excludedCategories => {
                                const maybeIndex = excludedCategories.findIndex(
                                    cat => cat.id === parseInt(row.id)
                                );

                                return maybeIndex === -1
                                    ? [...excludedCategories, { isActive: false, id: parseInt(row.id) }]
                                    : [
                                          ...excludedCategories.slice(0, maybeIndex),
                                          ...excludedCategories.slice(maybeIndex + 1),
                                      ];
                            })
                        }
                    />
                );
            },
        }),
        columnHelper.accessor(row => row.documentCategoryName, {
            id: 'documentCategoryName',
            meta: {
                filter: {
                    search: {
                        placeholder: 'Document Category',
                        type: 'search',
                    },
                },
            },
        }),
    ];

    const table = useTable({
        data: data?.items ?? [],
        columns: columnDefs,
        getCoreRowModel: getCoreRowModel(),
        onColumnFiltersChange: columnFilters => {
            setPagination(pagination => ({
                ...pagination,
                pageIndex: 0,
            }));
            setColumnFilters(columnFilters);
        },
        state: { pagination, columnFilters },
        filterFns: {},
        manualPagination: true,
        enableRowSelection: false,
        onPaginationChange: setPagination,
        rowCount: data?.total ?? undefined,
        getRowId: row => row.id.toString(),
    });

    const email = 'requestmanager@datavant.com';

    const NoFeedbackOverlay = () => (
        <VStack height={'200px'}>
            <AbsoluteCenter
                transition={'all 250ms ease-in-out'}
                axis={'both'}
                backdropFilter={'blur(10px)'}
                width={'100%'}
                height={'100%'}
            >
                <Center height={'100%'}>
                    <Text.Status size={'xs'} appearance={'negative'} align={'center'}>
                        Unable to load scanned document category data, please reach out to{' '}
                        <Text.Interactive appearance={'prominent'} size={'xs'} as={'span'}>
                            <Link href={`mailto:${email}`}>{email}</Link>
                        </Text.Interactive>{' '}
                        to proceed.
                    </Text.Status>
                </Center>
            </AbsoluteCenter>
        </VStack>
    );

    const NotEditingBody = (
        <VStack gap={'16px'} alignItems={'flex-start'}>
            {dataNotAvailable ? (
                <NoFeedbackOverlay />
            ) : (
                <>
                    <Text.Contrast appearance={'medium'} size={'sm'}>
                        Datavant has pre-selected the recommended scanned document categories based on your
                        EMR and data extraction to optimize the AutoFulfill process.
                    </Text.Contrast>
                    <Button
                        buttonAppearance={'ghost'}
                        intent={'neutral'}
                        rightIcon={<FontAwesomeIcon icon={isTableOpen ? faCaretUp : faCaretDown} />}
                        onClick={toggleTable}
                    >
                        Default Scanned Document Categories
                    </Button>
                    <Collapse in={isTableOpen}>
                        <Table
                            table={table}
                            isLoading={isLoading}
                            columnDefs={columnDefs}
                            tableAppearance={'ghost'}
                            headerOptions={{
                                showResetButton: false,
                                showExportButton: false,
                                showRefreshButton: false,
                            }}
                        ></Table>
                    </Collapse>

                    <Text.Contrast appearance={'medium'} size={'sm'}>
                        We recommend using these default settings for the best results. Applying additional
                        filters outside of this default may reduce provider yield and increase processing
                        time.
                    </Text.Contrast>
                    <Alert
                        description={
                            <VStack alignItems={'flex-start'} gap={0}>
                                <Box>
                                    If you need to modify these default settings, please note that it may take
                                    up to 2 additional weeks to activate AutoFulfill.
                                </Box>
                                <Box>You can always adjust these settings later if needed.</Box>
                            </VStack>
                        }
                    />
                </>
            )}
        </VStack>
    );

    const EditingBody = (
        <VStack alignItems={'flex-start'} gap={'16px'}>
            <Alert
                status={'negative'}
                description={
                    'If you need to modify these default settings, please note that it may take up to 2 additional weeks to activate AutoFulfill. '
                }
            />
            <Table
                table={table}
                isLoading={isLoading}
                columnDefs={columnDefs}
                tableAppearance={'ghost'}
                onResetClick={refetch}
                headerOptions={{
                    showResetButton: true,
                    showExportButton: false,
                    showRefreshButton: false,
                    belowHeaderContent: (
                        <HStack pb={'2px'}>
                            <FontAwesomeIcon
                                icon={faTriangleExclamation}
                                color={UiKit.consistency.warning70}
                            />
                            <Text.Status size={'xs'} appearance={'caution'}>
                                {`${excludedCategories.length} modification(s)`}
                            </Text.Status>
                        </HStack>
                    ),
                }}
            ></Table>
        </VStack>
    );

    return (
        <ChakraProvider>
            <Modal
                isOpen={isOpen}
                onClose={onClose}
                size={'md'}
                header={
                    <Text.Contrast appearance={'high'} size={'xl'}>
                        Verify Scanned Document Categories
                    </Text.Contrast>
                }
                body={!isLoading && (!isEditing ? NotEditingBody : EditingBody)}
                Footer={() => (
                    <ModalFooter
                        action={
                            <Button
                                intent={!isEditing ? 'prominent' : 'negative'}
                                isLoading={isLoading}
                                onClick={() => {
                                    mutate(
                                        excludedCategories.map(cat => ({
                                            active: cat.isActive,
                                            id: cat.id,
                                        }))
                                    );
                                }}
                                isDisabled={dataNotAvailable}
                            >
                                {!isEditing ? 'Use Default Settings' : 'Proceed with Modifications'}
                            </Button>
                        }
                        cancel={
                            !isEditing ? (
                                <Button
                                    buttonAppearance={'ghost'}
                                    intent={'negative'}
                                    onClick={toggleEditing}
                                    isLoading={isLoading}
                                    isDisabled={dataNotAvailable}
                                >
                                    Modify Settings
                                </Button>
                            ) : (
                                <></>
                            )
                        }
                        back={
                            <Button
                                buttonAppearance={'ghost'}
                                intent={'neutral'}
                                onClick={() => {
                                    if (!isEditing) {
                                        onClose();
                                    } else {
                                        toggleEditing();
                                        void refetch();
                                    }
                                }}
                            >
                                {!isEditing ? 'Close' : 'Back to Default Settings'}
                            </Button>
                        }
                    />
                )}
            />
        </ChakraProvider>
    );
};
